/** 包裹式：选区已带 prefix..suffix 时切换为去除；否则包裹（空选插入占位） */
export function wrapSelection(value, s, e, prefix, suffix, placeholder) {
    const selected = value.slice(s, e);
    const isWrapped = selected.startsWith(prefix) && selected.endsWith(suffix);
    const inner = isWrapped ? selected.slice(prefix.length, -suffix.length) : selected || placeholder;
    const text = value.slice(0, s) + prefix + inner + suffix + value.slice(e);
    return { text, sel: [s + prefix.length, s + prefix.length + inner.length] };
}
/** 行级前缀：作用于选区所在行（无选区即光标行）；已带前缀的行做去除切换 */
export function prefixLines(value, s, e, prefix) {
    const lineStart = value.lastIndexOf('\n', s) + 1;
    let lineEnd = value.indexOf('\n', e);
    if (lineEnd === -1)
        lineEnd = value.length;
    const block = value.slice(lineStart, lineEnd);
    const replaced = block
        .split('\n')
        .map((line) => line.trim())
        .map((line) => {
        if (!line)
            return line;
        return line.startsWith(prefix) ? line.slice(prefix.length) : prefix + line;
    })
        .join('\n');
    const text = value.slice(0, lineStart) + replaced + value.slice(lineEnd);
    return { text, sel: [lineStart, lineStart + replaced.length] };
}
/** 生成工具栏动作表（占位文案传入，便于双端统一措辞） */
export function createToolbarActions(t) {
    return [
        {
            key: 'bold',
            faClass: 'fa-solid fa-bold',
            title: t('bold'),
            transform: (v, s, e) => wrapSelection(v, s, e, '**', '**', t('bold')),
        },
        {
            key: 'italic',
            faClass: 'fa-solid fa-italic',
            title: t('italic'),
            transform: (v, s, e) => wrapSelection(v, s, e, '*', '*', t('italic')),
        },
        {
            key: 'strikethrough',
            faClass: 'fa-solid fa-strikethrough',
            title: t('strikethrough'),
            transform: (v, s, e) => wrapSelection(v, s, e, '~~', '~~', t('strikethrough')),
        },
        {
            key: 'heading',
            faClass: 'fa-solid fa-heading',
            title: t('heading'),
            transform: (v, s, e) => prefixLines(v, s, e, '## '),
        },
        {
            key: 'quote',
            faClass: 'fa-solid fa-quote-right',
            title: t('quote'),
            transform: (v, s, e) => prefixLines(v, s, e, '> '),
        },
        {
            key: 'inlineCode',
            faClass: 'fa-solid fa-code',
            title: t('inlineCode'),
            transform: (v, s, e) => wrapSelection(v, s, e, '`', '`', t('inlineCode')),
        },
        {
            key: 'codeBlock',
            faClass: 'fa-solid fa-file-code',
            title: t('codeBlock'),
            transform: (v, s, e) => wrapSelection(v, s, e, '```\n', '\n```', t('codeBlock')),
        },
        {
            key: 'link',
            faClass: 'fa-solid fa-link',
            title: t('link'),
            transform: (v, s, e) => {
                const selected = v.slice(s, e) || t('linkText');
                const before = v.slice(0, s) + `[${selected}](`;
                const after = `)` + v.slice(e);
                return {
                    text: before + t('linkUrl') + after,
                    sel: [s + selected.length + 3, s + selected.length + 3 + t('linkUrl').length],
                };
            },
        },
        {
            key: 'image',
            faClass: 'fa-solid fa-image',
            title: t('image'),
            transform: (v, s, e) => {
                // Obsidian 尺寸语法（R2）：默认宽 300，占位预选中便于直接输入
                const part = `![${t('imageAlt')}|300](${t('imageUrl')})`;
                const text = v.slice(0, s) + part + v.slice(e);
                return { text, sel: [s, s + part.length] };
            },
        },
        {
            key: 'taskList',
            faClass: 'fa-solid fa-list-check',
            title: t('taskList'),
            transform: (v, s, e) => prefixLines(v, s, e, '- [ ] '),
        },
        {
            key: 'ulList',
            faClass: 'fa-solid fa-list-ul',
            title: t('ulList'),
            transform: (v, s, e) => prefixLines(v, s, e, '- '),
        },
        {
            key: 'olList',
            faClass: 'fa-solid fa-list-ol',
            title: t('olList'),
            transform: (v, s, e) => prefixLines(v, s, e, '1. '),
        },
        {
            key: 'table',
            faClass: 'fa-solid fa-table',
            title: t('table'),
            transform: (v, s, e) => {
                const tpl = `\n| ${t('tableCol1')} | ${t('tableCol2')} |\n| --- | --- |\n| ${t('tableCell')} | ${t('tableCell')} |\n`;
                const text = v.slice(0, s) + tpl + v.slice(e);
                return { text, sel: [s + 2, s + 2] };
            },
        },
        {
            key: 'divider',
            faClass: 'fa-solid fa-ellipsis',
            title: t('divider'),
            transform: (v, s, e) => {
                const text = v.slice(0, s) + (s > 0 ? '\n' : '') + '---\n' + v.slice(e);
                return { text, sel: [s, s] };
            },
        },
    ];
}
/** 工具栏中文文案表（双端默认词典，可覆盖） */
export function defaultToolbarLabels(key) {
    const labels = {
        bold: '加粗',
        italic: '斜体',
        strikethrough: '删除线',
        heading: '标题',
        quote: '引用',
        inlineCode: '行内代码',
        codeBlock: '代码块',
        link: '链接',
        linkText: '链接文字',
        linkUrl: 'https://',
        image: '图片',
        imageAlt: '图片描述',
        imageUrl: '图片地址',
        taskList: '任务列表',
        ulList: '无序列表',
        olList: '有序列表',
        table: '表格',
        tableCol1: '列一',
        tableCol2: '列二',
        tableCell: '内容',
        divider: '分隔线',
    };
    return labels[key] ?? key;
}
//# sourceMappingURL=toolbar.js.map